import React from "react";
import { cn } from "../../DashboardPage/lib/utils";
import { SkeletonTableRows } from "./skeleton";

/**
 * The canonical table pattern.
 *
 * Two ways to use it:
 *
 * 1. Declarative — pass `columns` + `rows` and let DataTable render the body.
 *    Best for simple tables (see the Users table on /dashboard).
 *
 * 2. Composed — pass `columns` (or a custom `head`) and supply `children` as the
 *    <tbody> contents. Needed when rows are their own components that render
 *    their own <tr>, e.g. DashboardTableRow.
 *
 * Either way the header style, row hover, borders, and loading/empty states are
 * shared, so tables cannot drift apart again.
 */

export interface DataTableColumn<T = any> {
  /** Stable key; also used as the React key for the header cell. */
  key: string;
  header: React.ReactNode;
  /** Cell renderer. Omit when using the composed form. */
  cell?: (row: T, index: number) => React.ReactNode;
  align?: "left" | "right" | "center";
  /** Extra classes applied to both the <th> and its <td>s. */
  className?: string;
}

const alignClass = (align: DataTableColumn["align"]) =>
  align === "right"
    ? "text-right"
    : align === "center"
      ? "text-center"
      : "text-left";

/** Shared cell padding, so every table on every page lines up. */
export const dataTableCellClass = "px-4 py-3";

interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  /** Declarative form: the rows to render. Ignored when `children` is given. */
  rows?: T[];
  /** Composed form: <tbody> contents supplied by the caller. */
  children?: React.ReactNode;
  getRowKey?: (row: T, index: number) => React.Key;
  loading?: boolean;
  /** How many placeholder rows to show while loading. Match the page size. */
  skeletonRows?: number;
  /** Shown when not loading and there are no rows. */
  emptyMessage?: React.ReactNode;
  /** Replaces the generated <thead> entirely (e.g. role-dependent headers). */
  head?: React.ReactNode;
  /** Applied to the scroll container. */
  className?: string;
  /** Forwarded to the <table> element. */
  tableId?: string;
  /** Forwarded to the scroll container, for tour targeting etc. */
  "data-tour"?: string;
  /** Declarative form: rows to render in grey rather than black. */
  isRowMuted?: (row: T, index: number) => boolean;
  /** Declarative form: a full-width row rendered above this one, e.g. a group divider. */
  renderBeforeRow?: (row: T, index: number, colSpan: number) => React.ReactNode;
}

export default function DataTable<T = any>({
  columns,
  rows,
  children,
  getRowKey,
  loading = false,
  skeletonRows = 5,
  emptyMessage = "No data found.",
  head,
  className,
  tableId,
  isRowMuted,
  renderBeforeRow,
  ...rest
}: DataTableProps<T>) {
  const colSpan = columns.length || 1;

  // In the composed form the caller owns the body, so emptiness is theirs to
  // decide; we only manage state rows for the declarative form.
  const isComposed = children !== undefined;
  const showEmpty = !loading && !isComposed && (rows?.length ?? 0) === 0;

  return (
    <div
      className={cn(
        "overflow-x-auto bg-white border border-neutral-200 rounded-lg",
        className
      )}
      {...rest}
    >
      <table
        id={tableId}
        className="min-w-full border-collapse whitespace-nowrap"
      >
        {head ?? (
          <thead>
            <tr className="border-b border-neutral-200 bg-primary-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    dataTableCellClass,
                    "text-xs font-semibold tracking-wide uppercase text-primary-900",
                    alignClass(col.align),
                    col.className
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {loading ? (
            // Skeleton rows rather than a centred spinner: the table keeps its
            // height and column widths, so rows fill in instead of the layout
            // jumping when the request lands.
            <SkeletonTableRows
              rows={skeletonRows}
              // Alignments, not just a count, so each placeholder sits under
              // its own header.
              columns={columns.length ? columns.map((col) => col.align) : colSpan}
              cellClassName={dataTableCellClass}
            />
          ) : showEmpty ? (
            <tr>
              <td
                className="px-4 py-10 text-sm text-center text-neutral-500"
                colSpan={colSpan}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : isComposed ? (
            children
          ) : (
            rows!.map((row, index) => (
              <React.Fragment key={getRowKey ? getRowKey(row, index) : index}>
                {renderBeforeRow?.(row, index, colSpan)}
                <tr className="transition-colors border-b border-neutral-100 last:border-b-0 hover:bg-primary-50">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        dataTableCellClass,
                        "text-sm",
                        isRowMuted?.(row, index) ? "text-neutral-400" : "text-black",
                        alignClass(col.align),
                        col.className
                      )}
                    >
                      {col.cell ? col.cell(row, index) : null}
                    </td>
                  ))}
                </tr>
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Row/cell classes for the composed form, so caller-rendered <tr>/<td> match
 * the declarative ones exactly.
 */
export const dataTableRowClass =
  "transition-colors border-b border-neutral-100 last:border-b-0 hover:bg-primary-50";

export const dataTableTdClass = cn(dataTableCellClass, "text-sm text-black");

/*
 * Row action icons. Both read neutral at rest and only take on meaning when
 * the user is about to interact: a destructive icon that is red by default
 * makes every row look alarming.
 */
export const rowActionIconClass =
  "border border-transparent inline-flex items-center justify-center p-1.5 rounded-md text-neutral-400 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-neutral-400 focus-visible:border-primary-700";

export const rowActionDangerIconClass =
  "border border-transparent inline-flex items-center justify-center p-1.5 rounded-md text-neutral-400 transition-colors hover:bg-danger-50 hover:text-danger-600 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-neutral-400 focus-visible:border-danger-500";

/** <th> classes for callers supplying a custom `head`. */
export const dataTableThClass = cn(
  dataTableCellClass,
  "text-xs font-semibold tracking-wide uppercase text-primary-900 text-left"
);

export const dataTableTheadRowClass = "border-b border-neutral-200 bg-primary-50";
